import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Table,
  TextField,
  Button,
  PlusIcon,
  ResetIcon,
  IconButton,
  DeleteOutlineIcon,
  FormHelperText,
  Autocomplete,
  ToolTip,
  Typography,
  HelpOutlineIcon
} from '@esgian/esgianui';
import { useTheme } from '@hooks';
import { FuelSelect } from '@components';

const getUsedPortIndices = (voyagePortCalls, tableData, currentRowIndex) => {
  const usedIndices = new Set();
  const startPointIndices = new Set();
  const endPointIndices = new Set();

  tableData.forEach((row, index) => {
    if (index === currentRowIndex) return;

    const { fromPort, toPort } = row;
    if (fromPort && toPort) {
      const fromIndex = voyagePortCalls.findIndex((port) => port.portId === fromPort.portId);
      const toIndex = voyagePortCalls.findIndex((port) => port.portId === toPort.portId);

      if (fromIndex !== -1 && toIndex !== -1) {
        for (let i = fromIndex; i <= toIndex; i++) {
          usedIndices.add(i);
        }
        startPointIndices.add(fromIndex);
        endPointIndices.add(toIndex);
      }
    }
  });

  return { usedIndices, startPointIndices, endPointIndices };
};

const getFirstAvailablePort = (voyagePortCalls, tableData) => {
  if (!voyagePortCalls || !voyagePortCalls.length) return null;
  if (tableData.length === 0 && voyagePortCalls.length > 0) return voyagePortCalls[0];
  const { usedIndices } = getUsedPortIndices(voyagePortCalls, tableData, -1);
  const firstAvailableIndex = usedIndices.size > 0 ? [...usedIndices][usedIndices.size - 1] : -1;
  return firstAvailableIndex !== -1 ? voyagePortCalls[firstAvailableIndex] : null;
};

const getLastAvailablePort = (voyagePortCalls, tableData, fromPort) => {
  if (!voyagePortCalls || !voyagePortCalls.length || !fromPort) return null;

  const { usedIndices } = getUsedPortIndices(voyagePortCalls, tableData, -1);
  const fromIndex = voyagePortCalls.findIndex((port) => port.portId === fromPort.portId);

  if (fromIndex === -1) return null;

  let lastAvailableIndex = fromIndex;

  for (let i = fromIndex + 1; i < voyagePortCalls.length; i++) {
    if (usedIndices.has(i)) break;
    lastAvailableIndex = i;
  }

  return voyagePortCalls[lastAvailableIndex];
};

const getInitialTableData = (defaultFuelConfiguration, voyagePortCalls, allFuelTypes) => {
  if (!defaultFuelConfiguration || !voyagePortCalls || !voyagePortCalls.length) {
    return [];
  }

  const initialData = defaultFuelConfiguration.map(({ fromPortId, toPortId, fuelTypeId }) => {
    const fromPort = voyagePortCalls.find((port) => port.portId === fromPortId);
    const toPort = voyagePortCalls.find((port) => port.portId === toPortId);

    return {
      fuelType: allFuelTypes.find((f) => f.id === fuelTypeId),
      fromPort,
      toPort
    };
  });

  return initialData;
};

const createRow = (voyagePortCalls, existingTableData, allFuelTypes) => {
  if (!voyagePortCalls || !voyagePortCalls.length) {
    return {
      fuelType: allFuelTypes[0],
      fromPort: null,
      toPort: null
    };
  }

  const firstAvailablePort = getFirstAvailablePort(voyagePortCalls, existingTableData);

  if (!firstAvailablePort) {
    return {
      fuelType: allFuelTypes[0],
      fromPort: null,
      toPort: null
    };
  }

  const lastAvailablePort = getLastAvailablePort(
    voyagePortCalls,
    existingTableData,
    firstAvailablePort
  );

  return {
    fuelType: allFuelTypes[0],
    fromPort: firstAvailablePort,
    toPort: lastAvailablePort || firstAvailablePort
  };
};

// Helper function to check for overlapping port ranges and adjust if needed
const enforceNonOverlappingRanges = (updatedTableData, voyagePortCalls) => {
  // Sort by the fromPort index in the voyage for display purposes only
  const sortedData = [...updatedTableData].sort((a, b) => {
    if (!a.fromPort) return 1;
    if (!b.fromPort) return -1;

    const aIndex = voyagePortCalls.findIndex((port) => port.portId === a.fromPort.portId);
    const bIndex = voyagePortCalls.findIndex((port) => port.portId === b.fromPort.portId);

    return aIndex - bIndex;
  });

  return sortedData;
};

const EditableMainEngineFuelTable = ({
  voyagePortCalls,
  onTableDataChange,
  errorMessage,
  defaultFuelConfiguration,
  title,
  titleTooltip,
  supportedFuels,
  allFuelTypes,
  tableData,
  setTableData
}) => {
  const { theme } = useTheme();
  const [validationErrors, setValidationErrors] = useState([]);

  // Check for same port in origin and destination
  const findSameOriginDestination = (data) => {
    return data
      .map((row, index) => {
        if (row.fromPort && row.toPort && row.fromPort.portId === row.toPort.portId) {
          return {
            rowIndex: index,
            errorType: 'samePort',
            message: 'Select different origin and destination'
          };
        }
        return null;
      })
      .filter(Boolean);
  };

  // Validate the table data
  const validateTableData = (data) => {
    if (!data || !data.length) {
      setValidationErrors([]);
      return true;
    }

    const samePortErrors = findSameOriginDestination(data);
    const allErrors = [...samePortErrors];
    setValidationErrors(allErrors);
    return allErrors.length === 0;
  };

  // Transform tableData to the required format before sending to the parent
  const transformTableData = (data) => {
    if (!data || !data.length || !voyagePortCalls || !voyagePortCalls.length) {
      return { formattedData: [], isAllPortsIncluded: false };
    }

    const formattedData = data
      .filter((row) => row.fromPort && row.toPort && row.fuelType)
      .map((row) => {
        const fromPortCall = voyagePortCalls.find((port) => port.portId === row.fromPort.portId);
        const toPortCall = voyagePortCalls.find((port) => port.portId === row.toPort.portId);

        if (!fromPortCall || !toPortCall) return null;

        return {
          fromPortId: fromPortCall.portId,
          fromPortDateTime: fromPortCall.arrivalDate,
          toPortId: toPortCall.portId,
          toPortDateTime: toPortCall.arrivalDate,
          fuelTypeId: row.fuelType.id
        };
      })
      .filter(Boolean);

    return {
      formattedData,
      isAllPortsIncluded: true,
      hasValidationErrors: validationErrors.length > 0
    };
  };

  useEffect(() => {
    validateTableData(tableData);
    const { formattedData, isAllPortsIncluded, hasValidationErrors } =
      transformTableData(tableData);
    onTableDataChange(formattedData, isAllPortsIncluded, hasValidationErrors);
  }, [tableData]);

  const updateTableData = (newData) => {
    const adjustedData = enforceNonOverlappingRanges(newData, voyagePortCalls);
    setTableData(adjustedData);
  };

  const getRowErrors = (rowIndex) => {
    return validationErrors.filter((error) => error.rowIndex === rowIndex);
  };

  const hasErrorType = (rowIndex, errorType) => {
    return validationErrors.some(
      (error) => error.rowIndex === rowIndex && error.errorType === errorType
    );
  };

  const getRowErrorMessage = (rowIndex) => {
    const errors = getRowErrors(rowIndex);
    return errors.length > 0 ? errors[0].message : '';
  };

  const columns = [
    {
      name: 'fuelType',
      label: 'Fuel Type',
      options: {
        customBodyRender: (value, { rowIndex }) => (
          <FuelSelect
            supportedFuels={supportedFuels}
            selectedFuel={value}
            handleChange={(newValue) => {
              setTableData((prev) =>
                prev.map((data, i) => (i === rowIndex ? { ...data, fuelType: newValue } : data))
              );
            }}
            size="small"
            variant="standard"
            disablePortal={false}
            error={hasErrorType(rowIndex, 'multipleFuels')}
          />
        )
      }
    },
    {
      name: 'fromPort',
      label: 'From Port',
      options: {
        customBodyRender: (value, { rowIndex }) => {
          // Get the ports that are already used in other rows
          const { usedIndices, startPointIndices, endPointIndices } = getUsedPortIndices(
            voyagePortCalls,
            tableData,
            rowIndex
          );

          // Check if row has any validation errors
          const hasError =
            hasErrorType(rowIndex, 'duplicate') ||
            hasErrorType(rowIndex, 'samePort') ||
            hasErrorType(rowIndex, 'reverseOrder');

          return (
            <Box>
              <Autocomplete
                options={voyagePortCalls || []}
                getOptionLabel={(option) => option.portName || ''}
                value={value}
                onChange={(_, newValue) => {
                  // When fromPort changes, we need to update the toPort as well to avoid overlaps
                  let updatedToPort = tableData[rowIndex].toPort;

                  if (newValue) {
                    const newFromIndex = voyagePortCalls.findIndex(
                      (port) => port.portId === newValue.portId
                    );
                    const currentToPort = tableData[rowIndex].toPort;
                    const currentToIndex = currentToPort
                      ? voyagePortCalls.findIndex((port) => port.portId === currentToPort.portId)
                      : -1;

                    // If the new fromPort is after the current toPort or toPort is already used,
                    // update toPort to the next available port after fromPort
                    if (
                      currentToIndex === -1 ||
                      newFromIndex > currentToIndex ||
                      usedIndices.has(currentToIndex)
                    ) {
                      updatedToPort = getLastAvailablePort(voyagePortCalls, tableData, newValue);
                    }
                  }

                  const updatedData = tableData.map((data, i) =>
                    i === rowIndex
                      ? {
                          ...data,
                          fromPort: newValue,
                          toPort: updatedToPort
                        }
                      : data
                  );

                  // Use the new function to update table data safely
                  updateTableData(updatedData);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    placeholder="Select port"
                    error={hasError}
                  />
                )}
                getOptionDisabled={(option) => {
                  const optionIndex = voyagePortCalls.findIndex(
                    (port) => port.portId === option.portId
                  );
                  if (startPointIndices.has(optionIndex)) {
                    return true;
                  } else if (usedIndices.has(optionIndex)) {
                    if (endPointIndices.has(optionIndex)) {
                      return false;
                    }
                    return true;
                  }
                  return false;
                }}
                isOptionEqualToValue={(option, value) => option.portId === value?.portId}
                size="small"
                disableClearable
              />
              {hasError && <FormHelperText error>{getRowErrorMessage(rowIndex)}</FormHelperText>}
            </Box>
          );
        }
      }
    },
    {
      name: 'toPort',
      label: 'To Port',
      options: {
        customBodyRender: (value, { rowIndex }) => {
          const currentFromPort = tableData[rowIndex]?.fromPort;
          // Check if row has any validation errors
          const hasError =
            hasErrorType(rowIndex, 'duplicate') ||
            hasErrorType(rowIndex, 'samePort') ||
            hasErrorType(rowIndex, 'reverseOrder');

          if (!currentFromPort) {
            return (
              <Box>
                <Autocomplete
                  options={[]}
                  getOptionLabel={(option) => option.portName || ''}
                  value={value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      placeholder="Select from port first"
                      error={hasError}
                    />
                  )}
                  disabled={true}
                  size="small"
                  disableClearable
                />
                {hasError && <FormHelperText error>{getRowErrorMessage(rowIndex)}</FormHelperText>}
              </Box>
            );
          }

          const fromPortIndex = voyagePortCalls.findIndex(
            (port) => port.portId === currentFromPort.portId
          );

          // Get the ports that are already used in other rows
          const { usedIndices } = getUsedPortIndices(voyagePortCalls, tableData, rowIndex);

          return (
            <Box>
              <Autocomplete
                options={voyagePortCalls || []}
                getOptionLabel={(option) => option.portName || ''}
                value={value}
                onChange={(_, newValue) => {
                  const updatedData = tableData.map((data, i) =>
                    i === rowIndex ? { ...data, toPort: newValue } : data
                  );

                  // Use the new function to update table data safely
                  updateTableData(updatedData);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    placeholder="Select port"
                    error={hasError}
                  />
                )}
                getOptionDisabled={(option) => {
                  // Disable options that:
                  // 1. Come before the fromPort
                  const optionIndex = voyagePortCalls.findIndex(
                    (port) => port.portId === option.portId
                  );
                  // 2. Are already used in other rows (but not end points)
                  return optionIndex < fromPortIndex || usedIndices.has(optionIndex);
                }}
                isOptionEqualToValue={(option, value) => option.portId === value?.portId}
                size="small"
                disableClearable
              />
              {hasError && <FormHelperText error>{getRowErrorMessage(rowIndex)}</FormHelperText>}
            </Box>
          );
        }
      }
    },
    {
      name: 'action',
      label: 'Action',
      options: {
        customBodyRender: (_, { rowIndex }) => {
          return (
            <IconButton
              onClick={() => setTableData((prev) => prev.filter((_, i) => i !== rowIndex))}>
              <DeleteOutlineIcon />
            </IconButton>
          );
        }
      }
    }
  ];

  const options = {
    selectableRows: 'none',
    responsive: 'standard',
    filter: false,
    search: false,
    download: false,
    tableBodyMaxHeight: '250px',
    print: false,
    viewColumns: false,
    pagination: false,
    elevation: 0,
    toolbar: true,
    customFooter: () => (
      <Box p={2} display="flex" flexDirection="column" gap={2}>
        <Box display="flex" gap={2}>
          <Button
            variant="text"
            startIcon={<PlusIcon />}
            disabled={!getFirstAvailablePort(voyagePortCalls, tableData)}
            onClick={() => {
              const newRow = createRow(voyagePortCalls, tableData, allFuelTypes);
              updateTableData([...tableData, newRow]);
            }}>
            Create a new record
          </Button>
          <Button
            variant="text"
            onClick={() =>
              setTableData(
                getInitialTableData(defaultFuelConfiguration, voyagePortCalls, allFuelTypes)
              )
            }
            startIcon={<ResetIcon />}>
            Reset
          </Button>
        </Box>
        {errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
        {validationErrors.length > 0 && (
          <FormHelperText error>Please fix validation errors highlighted in red</FormHelperText>
        )}
      </Box>
    ),
    textLabels: {
      body: {
        noMatch: 'No records found. Try creating a new one.'
      }
    }
  };

  return (
    <Table
      options={options}
      mode={theme.mode}
      title={
        <Box display="flex" alignItems="center" sx={{ marginLeft: '-10px', marginBottom: '-20px' }}>
          <Typography variant="h6">{title}</Typography>
          <ToolTip title={titleTooltip}>
            <IconButton size={'small'}>
              <HelpOutlineIcon sx={{ fontSize: '16px' }} />
            </IconButton>
          </ToolTip>
        </Box>
      }
      data={tableData}
      columns={columns}
    />
  );
};

EditableMainEngineFuelTable.propTypes = {
  voyagePortCalls: PropTypes.array,
  onTableDataChange: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  defaultFuelConfiguration: PropTypes.array,
  title: PropTypes.string,
  titleTooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  supportedFuels: PropTypes.array,
  allFuelTypes: PropTypes.array,
  tableData: PropTypes.array,
  setTableData: PropTypes.func,
  onTableDataUpdate: PropTypes.func
};

export default EditableMainEngineFuelTable;
